import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userManager } from "../lib/authContext";

/**
 * /auth/callback — handles Okta's OIDC redirect.
 *
 * Okta sends the user back here with `code` + `state` query params. The
 * UserManager exchanges the code for tokens (PKCE — no client secret),
 * persists them to localStorage, and emits `userLoaded` which the
 * AuthProvider listens to. After that we navigate back to wherever the
 * user was trying to reach (stashed in `state.returnTo` when signIn fired).
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    userManager
      .signinCallback(window.location.href)
      .then((user) => {
        if (cancelled) return;
        // user.state was set by signinRedirect({ state: { returnTo } });
        // fall back to the dashboard root if for any reason it isn't there.
        const returnTo =
          (user?.state as { returnTo?: string } | undefined)?.returnTo ?? "/";
        // Clean up the OIDC params from the URL via replace.
        navigate(returnTo, { replace: true });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-0)",
        color: "var(--text-mid)",
        fontFamily: "var(--mono)",
        fontSize: 13,
        gap: 12,
      }}
    >
      {error ? (
        <>
          <div style={{ color: "var(--danger)" }}>Sign-in failed</div>
          <div style={{ maxWidth: 480, textAlign: "center" }}>{error}</div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => navigate("/", { replace: true })}
          >
            Try again
          </button>
        </>
      ) : (
        <span>Finishing sign-in…</span>
      )}
    </div>
  );
}
